import { APIError, ChiefAPIError } from "@/backend/types/backendTypes";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { z } from "zod";
import { DEPLOY_ENVIRONMENT } from "@/constants/constants";
import { posthogFeatureFlagsAtom } from "@/atoms/store";
import { getDefaultStore } from "jotai/vanilla";
import axiosInstance from "@/lib/axios";

const PatchAdAccountCsidsSchema = z.object({
  mappings: z.array(
    z.object({
      source: z.union([
        z.literal("google"),
        z.literal("facebook"),
        z.literal("tiktok"),
        z.literal("pinterest"),
        z.literal("outbrain"),
        z.literal("msads"),
        z.literal("taboola"),
      ]),
      account_id: z.string(),
      associated_csids: z.array(z.string()),
    })
  ),
});
export type PatchAdAccountCsidsInput = z.infer<
  typeof PatchAdAccountCsidsSchema
>;

const onPatchCsids = async (input: PatchAdAccountCsidsInput) => {
  try {
    PatchAdAccountCsidsSchema.parse(input);
    const featureFlags = await getDefaultStore().get(posthogFeatureFlagsAtom);
    const headers: Record<string, string> = {
      "X-TRACIFY-ENV": DEPLOY_ENVIRONMENT,
    };

    if (featureFlags.flags?.outbrain_account_setup?.header_value) {
      headers["X-TRACIFY-FEATUREFLAGS"] =
        featureFlags.flags?.outbrain_account_setup?.header_value;
    }
    const response = await axiosInstance.post(
      `${process.env.NEXT_PUBLIC_AD_CONNECTOR_V1_BASE_URL}/account/update-store-mappings`,
      { mappings: input.mappings },
      {
        headers,
      }
    );
    if (response.status !== 200) {
      throw new Error(response?.data?.error ?? "Unknown error occurred.");
    }

    return response.data;
  } catch (err: any) {
    if (
      err &&
      typeof err === "object" &&
      "isAxiosError" in err &&
      err.isAxiosError
    ) {
      if (!err.response) {
        throw err;
      }
      const axiosError = err as AxiosError;
      const apiError = axiosError.response?.data as ChiefAPIError;
      const firstErr = apiError.detail?.[0] ?? "Unknown error occurred.";
      throw new Error(firstErr?.msg ?? axiosError.message);
    }
    if (err.message?.startsWith("Failed to connect the following account(s)")) {
      throw new Error(err.message);
    }
    throw new Error("Unkown error occurred!");
  }
};

export const usePatchAdAccountCsids = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: PatchAdAccountCsidsInput) => onPatchCsids(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["connectedAdAccounts"] });
    },
  });
};
